/* @flow */
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import theme from './UserDetails.scss';
import {
    personnelSelector,
    personnelIdSelector,
    personnelClientIdSelector
} from 'common/redux/selectors/sessionSelector';
import { personnelVersionSelector } from 'common/redux/selectors/personnelSelectors';
import { EditableInputField } from 'common/components/core/common/CustomEditableField';
import PersonnelActions from 'common/redux/actions/personnel';
import { PersonnelDetailType } from 'common/enums/PersonnelEnums';
import settingsTestIds from 'common/tests/mySettings/settings.json';

type InfoProps = {
    type: string,
    t: Function,
    personnelId: string,
    clientId: string,
    version: number,
    testId: string,
    patchUserDetails: Function,
    getValidationMessage: Function,
    // fn to transform value before saving to BE
    transformValue?: Function
};

type State = {
    value: string
};

class Info extends PureComponent<InfoProps, State> {
    constructor(props) {
        super(props);
        const { value } = props;
        this.state = {
            value
        };
    }

    onReject = () => {
        this.setState({
            value: this.props.value
        });
        return;
    };

    onSave = value => {
        const {
            patchUserDetails,
            personnelId,
            clientId,
            type,
            version,
            getValidationMessage,
            transformValue
        } = this.props;
        const params = {
            value: transformValue ? transformValue(value) : value,
            type,
            version
        };

        const message = getValidationMessage(value);

        if (message) {
            return new Promise((resolve, reject) => {
                reject({ message });
            });
        }

        return patchUserDetails(clientId, personnelId, params).then(() => {
            this.setState({
                value
            });
            return;
        });
    };

    render() {
        const { type, testId, t } = this.props;
        const label = t(`user.${type}`);
        const placeholder = t(`user.${type}Placeholder`);

        const { value } = this.state;

        return (
            <div className={theme.info}>
                <div className={theme.label}>{label}</div>
                <EditableInputField
                    theme={theme}
                    customFieldClass={theme.customEditableInput}
                    value={value}
                    isEditingEnabled
                    placeholder={placeholder}
                    onSave={this.onSave}
                    onReject={this.onReject}
                    testId={testId}
                />
            </div>
        );
    }
}

type Props = {
    t: Function,
    personnel: Object,
    personnelId: string,
    clientId: string,
    version: number,
    patchUserDetails: Function
};

class UserDetails extends PureComponent<Props> {
    getEmailValidationMessage = value => {
        const { personnel } = this.props;
        return personnel
            .setEmail(value)
            .getValidationMessage(PersonnelDetailType.Email);
    };

    getNameValidationMessage = value => {
        const { personnel } = this.props;
        return personnel
            .setName(value)
            .getValidationMessage(PersonnelDetailType.Name);
    };

    render() {
        const { personnel } = this.props;
        const { name, email } = personnel;
        return (
            <div className={theme.detailsContainer}>
                <Info
                    type={PersonnelDetailType.Name}
                    key={PersonnelDetailType.Name}
                    value={name}
                    getValidationMessage={this.getNameValidationMessage}
                    testId={settingsTestIds.name}
                    {...this.props}
                />
                <Info
                    type={PersonnelDetailType.Email}
                    key={PersonnelDetailType.Email}
                    value={email}
                    getValidationMessage={this.getEmailValidationMessage}
                    transformValue={value => ({ emailAddress: value })}
                    testId={settingsTestIds.email}
                    {...this.props}
                />
            </div>
        );
    }
}

const mapStateToProps = (state: Object, ownProps: Object) => {
    const personnel = personnelSelector(state);
    const personnelId = personnelIdSelector(state);
    const personnelClientId = personnelClientIdSelector(state);
    const personnelVersion = personnelVersionSelector(state);

    return {
        ...ownProps,
        personnel,
        personnelId,
        clientId: personnelClientId,
        version: personnelVersion
    };
};

const mapDispatchToProps = {
    patchUserDetails: PersonnelActions.patchUserDetails
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    translate('settings')
)(UserDetails);
